import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetCountryListByPage } from "../../../../../../api/services/common/commonApis";
import { templateListType } from "../../../../../../api/services/programTemplate/models";
import {
  useGetLineOfBusiness,
  useGetProgramTemplateListForCreateProgram,
} from "../../../../../../api/services/programTemplate/programTemplateApis";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiSelectWithTextInput,
  MuiTextField,
  OptionsDataType,
} from "../../../../../../components";
import {
  LANGUAGE_CODE,
  MAX_LENGTH_100,
  PAGE_NAME_FOR_COUNTRY_DROPDOWN,
  PAGE_OPERATION_TYPE,
  SOLUTION_OFFERING_CMC_SITECORE_KEY,
} from "../../../../../../helpers/constants";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { useSyncFormValidity } from "../../../../../../hooks/useSyncFormValidity/useSyncFormValidity";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { setBulkTransferSearchInputValues } from "../../../../../../redux/slices/program/programSlice";
import {
  SolutionOfferingsResponseType,
  TransferSearchParticipantFormKey,
  transferTypeKey,
} from "../../../../modelsAndSchemas/Models";
import { bulkTransferSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { DropDownData } from "../../../../../../api/services/common/models";

export interface SearchParticipantsFormProps {
  pageId: string;
  selectedTransferType: string;
  isParticipantListChanged: boolean;
  solutionOfferingData: SolutionOfferingsResponseType[];
}

export const SearchParticipantsForm: React.FC<SearchParticipantsFormProps> = ({
  pageId,
  selectedTransferType,
  isParticipantListChanged,
  solutionOfferingData,
}) => {
  const dispatch = useDispatch();
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const {
    control,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useFormContext();
  const { commonLabels, fetchLabel, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [nextViewText, setNextViewText] = useState("");
  const [templateList, setTemplateList] = useState<OptionsDataType[]>([]);
  const isSelectedTransferProgram =
    selectedTransferType === transferTypeKey.changeProgram;
  const { isMobile } = useMediaQueries();

  const {
    data: countryListData,
    isError: isCountryError,
    isFetching: isCountryFetching,
  } = useGetCountryListByPage(
    PAGE_NAME_FOR_COUNTRY_DROPDOWN.BULK_PROCESS,
    PAGE_OPERATION_TYPE.ADD,
  );

  const {
    data: lineOfBusinessData,
    isError: isLineOfBusinessError,
    isFetching: isBusinessFetching,
  } = useGetLineOfBusiness(LANGUAGE_CODE.EN_US);

  const {
    data: programTemplates,
    isError: isProgramError,
    isFetching: isProgramFetching,
    refetchWithParams: getProgramTemplateList,
  } = useGetProgramTemplateListForCreateProgram(templateListType.bulkTransfer);

  const mandatoryFields = [
    TransferSearchParticipantFormKey.deliveryCountry,
    TransferSearchParticipantFormKey.productLine,
    TransferSearchParticipantFormKey.programTemplate,
    ...(isSelectedTransferProgram
      ? []
      : [TransferSearchParticipantFormKey.nextView]),
  ];

  useSyncFormValidity({
    formKey: "searchParticipantForm",
    mandatoryFields,
    errors,
    isMobile,
  });

  const nextViewWatch = useWatch({
    control,
    name: TransferSearchParticipantFormKey.nextView,
  });

  const productLineWatch = useWatch({
    control,
    name: TransferSearchParticipantFormKey.productLine,
  });

  useEffect(() => {
    const { nextView, productLine } = getValues();
    if (isNotEmpty(nextView) && isNotEmpty(productLine)) {
      getProgramTemplateList({
        solutionOfferingsId: nextView,
        lobId: productLine,
      });
      setValue(TransferSearchParticipantFormKey.programTemplate, "");
    } else {
      if (isDirty) {
        setTemplateList([]);
        setValue(TransferSearchParticipantFormKey.programTemplate, "");
      }
    }
  }, [nextViewWatch, productLineWatch]);

  useEffect(() => {
    if (programTemplates) {
      setTemplateList(setApiDataToDropDownOption(programTemplates));
    }
  }, [programTemplates]);

  useEffect(() => {
    if (isLineOfBusinessError || isProgramError || isCountryError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
        }),
      );
    }
  }, [isLineOfBusinessError, isProgramError, isCountryError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isBusinessFetching || isProgramFetching || isCountryFetching,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    };
  }, [isBusinessFetching, isProgramFetching, isCountryFetching]);

  useEffect(() => {
    const { deliveryCountry, nextView, productLine, programTemplate } =
      getValues();
    dispatch(
      setBulkTransferSearchInputValues({
        nextViewId: nextView,
        deliveryCountryId: deliveryCountry,
        programTemplateId: programTemplate,
        productLineId: productLine,
        nextViewName:
          solutionOfferingData?.find(
            (item: SolutionOfferingsResponseType) => item.id === nextView,
          )?.name ?? "",
        deliveryCountryName:
          countryListData?.find(
            (item: DropDownData) => item.key === deliveryCountry,
          )?.title ?? "",
        programTemplateName:
          templateList?.find(
            (item: OptionsDataType) => item.value === programTemplate,
          )?.label ?? "",
      }),
    );
  }, [isParticipantListChanged]);

  const getSearchLabel = (key: string) => {
    return fetchLabel(bulkTransferSiteCoreKeys.searchParticipantsKey, key);
  };

  const filterDropdownValue = fetchDropdownValues(
    bulkTransferSiteCoreKeys.searchParticipantsKey,
    bulkTransferSiteCoreKeys.filterByList,
  );

  useEffect(() => {
    if (isNotEmpty(solutionOfferingData)) {
      const nextViewTextValue =
        solutionOfferingData?.find(
          (item: SolutionOfferingsResponseType) =>
            item.nameKey === SOLUTION_OFFERING_CMC_SITECORE_KEY,
        )?.name ?? "";
      setNextViewText(nextViewTextValue);
    }
  }, [solutionOfferingData]);

  return (
    <MuiBox>
      <MuiGrid
        container
        className="form-container"
        data-testid="search-participant-form"
      >
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={TransferSearchParticipantFormKey.deliveryCountry}
          >
            <MuiSelect
              label={getSearchLabel(bulkTransferSiteCoreKeys.deliveryCountry)}
              options={setApiDataToDropDownOption(countryListData, true)}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${TransferSearchParticipantFormKey.deliveryCountry}`}
              placeholder={commonLabels.selectPlaceHolder}
              isMandatory
              showSearch
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          {isSelectedTransferProgram ? (
            <MuiTextField
              value={nextViewText}
              disabled
              isMandatory
              label={getSearchLabel(bulkTransferSiteCoreKeys.nextView)}
              requiredARIALabel={commonARIALabel.required}
            />
          ) : (
            <FormController
              control={control}
              name={TransferSearchParticipantFormKey.nextView}
            >
              <MuiSelect
                label={getSearchLabel(bulkTransferSiteCoreKeys.nextView)}
                options={setApiDataToDropDownOption(solutionOfferingData)}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${TransferSearchParticipantFormKey.nextView}`}
                placeholder={commonLabels.selectPlaceHolder}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          )}
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={TransferSearchParticipantFormKey.productLine}
          >
            <MuiSelect
              label={getSearchLabel(bulkTransferSiteCoreKeys.productLine)}
              options={setApiDataToDropDownOption(lineOfBusinessData)}
              visibleDefaultSelectOption
              value=""
              showSearch
              id={`${pageId}-${TransferSearchParticipantFormKey.productLine}`}
              placeholder={commonLabels.selectPlaceHolder}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={TransferSearchParticipantFormKey.programTemplate}
          >
            <MuiSelect
              label={getSearchLabel(bulkTransferSiteCoreKeys.programTemplate)}
              options={templateList}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${TransferSearchParticipantFormKey.programTemplate}`}
              placeholder={commonLabels.selectPlaceHolder}
              isMandatory
              showSearch
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={TransferSearchParticipantFormKey.client}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getSearchLabel(bulkTransferSiteCoreKeys.client)}
              placeholder={commonLabels.inputPlaceHolder}
              control={control}
              selectBoxName={TransferSearchParticipantFormKey.clientFilter}
              id={`${pageId}-${TransferSearchParticipantFormKey.client}`}
              selectBoxId={`${pageId}-${TransferSearchParticipantFormKey.clientFilter}`}
              maxLength={MAX_LENGTH_100}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
